import ComplianceModule from "@/store/modules/compliance";
import DeviceModule from "@/store/modules/device";
import POSConfigModule from "@/store/modules/pos-config";
import UserModule from "@/store/modules/user";
import { computed } from "vue";
import {
  isAustriaCompliancePackageEnabled,
  isWebPosFiscalExempt,
} from "@/utils/helpers/feature-flags";

export const isGermanCompliant = computed(() => {
  const groupId = POSConfigModule.groupId;
  const countryCode = POSConfigModule.getGroupCountryCode;

  // German compliance is required for all groups in Germany except group 19679 and 19957
  if (countryCode === "DE" && groupId !== 19679 && groupId !== 19957) {
    return true;
  }
  return false;
});

/**
 * Spanish compliance is not yet to be released to production groups
 */
export const isSpanishCompliant = computed(() => {
  const groupId = POSConfigModule.groupId;
  const countryCode = POSConfigModule.getGroupCountryCode;
  const isTestGroup = groupId === 17125 && import.meta.env.VITE_ENV === "test";
  if (countryCode === "ES" && isTestGroup) {
    return true;
  }
  return false;
});

export const isCroatianCompliant = computed(() => {
  const countryCode = POSConfigModule.getGroupCountryCode;
  if (countryCode === "HR") {
    return true;
  }
  return false;
});

/**
 * French compliance is only active when VITE_IS_FRENCH_VERSION is set to "1" and the group country code is FR.
 * This environment variable should only be set in the following branches: fr-test and fr-prod.
 */
export const isFrenchCompliant = computed(() => {
  const countryCode = POSConfigModule.getGroupCountryCode;
  const isFrenchVersion = import.meta.env.VITE_IS_FRENCH_VERSION === "1";
  if (countryCode === "FR" && isFrenchVersion) {
    return true;
  }
  return false;
});

/**
 * Belgian compliance is only active when VITE_IS_BELGIAN_VERSION is set to "1" and the group country code is BE.
 * This environment variable should only be set in the Belgian builds, the same way VITE_IS_FRENCH_VERSION gates NF525.
 */
export const isBelgianCompliant = computed(() => {
  const countryCode = POSConfigModule.getGroupCountryCode;
  const isBelgianVersion = import.meta.env.VITE_IS_BELGIAN_VERSION === "1";
  if (countryCode === "BE" && isBelgianVersion) {
    return true;
  }
  return false;
});

/**
 * French groups running the desktop app on a build without
 * VITE_IS_FRENCH_VERSION are on the old, uncertified POS version and must be
 * warned to stop selling (FIS-405).
 *
 * The mirror of isFrenchCompliant above — same country, negated build flag —
 * restricted to the desktop app: on the web POS the sale is already blocked
 * by isWebPosBlocked, and "switch to the desktop app" is the actionable
 * message there, so only a desktop install on the wrong build gets this
 * warning.
 */
export const isUncertifiedFrenchVersion = computed(() => {
  const countryCode = POSConfigModule.getGroupCountryCode;
  const isFrenchVersion = import.meta.env.VITE_IS_FRENCH_VERSION === "1";
  if (countryCode === "FR" && isDesktopApp.value && !isFrenchVersion) {
    return true;
  }
  return false;
});

export const isAustrianCompliant = computed(() => {
  const countryCode = POSConfigModule.getGroupCountryCode;
  if (
    countryCode === "AT" &&
    DeviceModule.hardwareLocation &&
    DeviceModule.hardwareLocation.FiskalyCashRegisterId
  ) {
    return true;
  }
  return false;
});

/**
 * FIS-374 gradual rollout: Austrian groups move from the legacy
 * signAustriaReceipt GraphQL mutation to the @smeetz/compliance-common
 * pipeline one group at a time, through the pos::austria-compliance-package
 * Flagsmith flag. Both implementations ship together: this is the switch the
 * compliance helpers and the legacy signer read to decide which one runs, while
 * isAustrianCompliant stays the shared country/device check (receipts, legacy
 * signing). With the flag off, or Flagsmith unreachable, nothing changes for
 * the group.
 */
export const isAustrianPackageCompliant = computed(() => {
  return isAustrianCompliant.value && isAustriaCompliancePackageEnabled();
});

export const isItalianCompliant = computed(() => {
  const countryCode = POSConfigModule.getGroupCountryCode;
  if (
    countryCode === "IT" &&
    DeviceModule.hardwareLocation &&
    DeviceModule.hardwareLocation.FiskalyCashRegisterId
  ) {
    return true;
  }
  return false;
});

export const hasInvoiceEnabled = computed(() => {
  return (
    !isSpanishCompliant.value &&
    !isFrenchCompliant.value &&
    !isCroatianCompliant.value &&
    !isBelgianCompliant.value &&
    !isAustrianPackageCompliant.value
  );
});

export const showWindowsAppVersion = computed(() => {
  return (
    !isSpanishCompliant.value &&
    !isFrenchCompliant.value &&
    !isCroatianCompliant.value &&
    !isAustrianPackageCompliant.value
  );
});

export const canSellInCompliantCountry = computed(() => {
  // Austria on the package path signs against Fiskaly over the internet, with
  // no EFR or FDM to probe, so like Germany it only blocks on the package init.
  if (isGermanCompliant.value || isAustrianPackageCompliant.value) {
    return ComplianceModule.complianceStatus.packageInit;
  }
  // Belgium blocks sales when the FDM is unreachable, the same way the EFR
  // countries block on the EFR connection.
  if (isBelgianCompliant.value) {
    return (
      ComplianceModule.complianceStatus.packageInit &&
      ComplianceModule.complianceStatus.fdmConnection
    );
  }
  if (
    isSpanishCompliant.value ||
    isFrenchCompliant.value ||
    isCroatianCompliant.value
  ) {
    return (
      ComplianceModule.complianceStatus.packageInit &&
      ComplianceModule.complianceStatus.efrConnection
    );
  }
  return true;
});

// Austria is deliberately absent on both of its paths: the only consumer is
// the company legal info requirement in getGroupInfo, which has no Austrian
// mapping, and a flag resolving after the config load would otherwise turn
// every Austrian receipt into "No company legal info found".
export const isInCompliantCountry = computed(() => {
  return (
    isGermanCompliant.value ||
    isSpanishCompliant.value ||
    isFrenchCompliant.value ||
    isCroatianCompliant.value ||
    isBelgianCompliant.value
  );
});

export const isAllowedToBypassCompliance = computed(() => {
  // colleagues are allowed to bypass compliance checks
  return UserModule.isColleague;
});

/**
 * The desktop (Electron) app exposes electronAPI and is the only runtime that
 * populates hardwareInfo.desktopAppVersion.
 */
export const isDesktopApp = computed(() => {
  return !!window.electronAPI || !!DeviceModule.hardwareInfo?.desktopAppVersion;
});

/**
 * Fiscal regulations require a certified POS in these countries; the web POS
 * is not certified there, so confirming sales is blocked (FIS-42).
 */
const WEB_POS_FISCALISED_COUNTRIES = ["IT", "AT", "DE", "FR", "BE", "ES", "HR"];

/**
 * Group users in fiscalised countries cannot confirm sales on the web POS and
 * must switch to the desktop app (FIS-42): the app stays fully usable —
 * browsing, orders, edits — but the sale progress/confirm button is disabled
 * and a persistent banner explains why. Exempt: colleague accounts (demos,
 * CAM) and groups with the Flagsmith web-pos-fiscal-exemption flag enabled,
 * whatever their country.
 */
export const isWebPosBlocked = computed(() => {
  const countryCode = POSConfigModule.getGroupCountryCode;
  if (!countryCode || !WEB_POS_FISCALISED_COUNTRIES.includes(countryCode)) {
    console.log("[FIS-42] isWebPosBlocked = false", {
      countryCode: countryCode || "(none)",
      reason: "country not fiscalised",
    });
    return false;
  }
  const blocked =
    !isDesktopApp.value &&
    !isAllowedToBypassCompliance.value &&
    !isWebPosFiscalExempt();
  console.log(`[FIS-42] isWebPosBlocked = ${blocked}`, {
    countryCode,
    groupId: POSConfigModule.groupId,
    isDesktopApp: isDesktopApp.value,
    hasElectronAPI: !!window.electronAPI,
    desktopAppVersion: DeviceModule.hardwareInfo?.desktopAppVersion || "(none)",
    isColleague: isAllowedToBypassCompliance.value,
    isFlagsmithExempt: isWebPosFiscalExempt(),
  });
  return blocked;
});
